import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Switch,
  TextField,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  Chip,
  Typography,
  Stack,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import InfoIcon from '@mui/icons-material/Info';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import Autocomplete from '@mui/material/Autocomplete';
import UnassignVoucherDialog, { UnassignVoucherPayload } from './UnassignVoucherDialog';
import { TravelItem, TravelStatus, TravelType } from '../types/travel';
import { searchWorkers, WorkerSummary, getWorkerWorkedHours, WorkedHoursResponseData, listVouchers, assignVoucher, unassignVoucher, createTravel, updateTravel, getTravelSettings, getTravel } from '../services/centralDbClient';

export interface NewTravelDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate?: (item: TravelItem) => void;
  onUpdate?: (item: TravelItem) => void;
  editId?: string; // when provided, dialog works in edit mode and loads by id
}

const toIso = (value: string | undefined): string | undefined => {
  if (!value) return undefined;
  try {
    const d = new Date(value);
    return isNaN(d.getTime()) ? undefined : d.toISOString();
  } catch {
    return undefined;
  }
};

export default function NewTravelDialog({ open, onClose, onCreate, onUpdate, editId }: NewTravelDialogProps) {
  const isEdit = !!editId;
  const [type, setType] = useState<TravelType>('flight');
  const [direction, setDirection] = useState<'leaving' | 'coming'>('leaving');
  const [personName, setPersonName] = useState('');
  const [selectedWorker, setSelectedWorker] = useState<WorkerSummary | null>(null);
  const [options, setOptions] = useState<WorkerSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [routeFrom, setRouteFrom] = useState('');
  const [routeTo, setRouteTo] = useState('');
  const [flightNumber, setFlightNumber] = useState('');
  const [departureLocal, setDepartureLocal] = useState(''); // yyyy-MM-ddTHH:mm
  const [arrivalLocal, setArrivalLocal] = useState('');
  const [status, setStatus] = useState<TravelStatus>('to_arrange');
  const [driverName, setDriverName] = useState('');
  const [infPerson, setInfPerson] = useState(false);
  const [infClient, setInfClient] = useState(false);
  const [infDriver, setInfDriver] = useState(false);
  // Taron vouchers
  const [isTaronFlight, setIsTaronFlight] = useState(false);
  const [voucherPreviewCode, setVoucherPreviewCode] = useState<string | undefined>(undefined);
  const [voucherAvailableCount, setVoucherAvailableCount] = useState<number>(0);
  const [assignedVoucher, setAssignedVoucher] = useState<import('../services/centralDbClient').TaronVoucher | null>(null);
  const [unassignOpen, setUnassignOpen] = useState(false);
  const pendingUpdateRef = useRef<{ id: string; payload: any } | null>(null);
  // Hours per ticket setting and input
  const [hoursPerTicketSetting, setHoursPerTicketSetting] = useState<number>(260);
  const [carAllowanceSetting, setCarAllowanceSetting] = useState<number>(50);
  const [hoursForTicket, setHoursForTicket] = useState<string>('');
  // Financials
  const [ticketPrice, setTicketPrice] = useState('');
  const [privatePaidTicket, setPrivatePaidTicket] = useState(false);
  const [ownContribution, setOwnContribution] = useState('');
  // Credits/Debits
  const [inCreditsDebits, setInCreditsDebits] = useState(false);
  const [creditsDebitsProcessedDate, setCreditsDebitsProcessedDate] = useState(''); // yyyy-MM-dd
  // Hours query range (separate from travel dates)
  const [hoursFromLocal, setHoursFromLocal] = useState(''); // yyyy-MM-dd
  const [hoursToLocal, setHoursToLocal] = useState('');     // yyyy-MM-dd
  const [workedHours, setWorkedHours] = useState<WorkedHoursResponseData | null>(null);
  const [workedHoursLoading, setWorkedHoursLoading] = useState(false);
  const [workedHoursError, setWorkedHoursError] = useState<string | null>(null);

  const canSubmit = useMemo(() => !!selectedWorker, [selectedWorker]);

  const reset = () => {
    setType('flight');
    setDirection('leaving');
    setPersonName('');
    setSelectedWorker(null);
    setOptions([]);
    setRouteFrom('');
    setRouteTo('');
    setFlightNumber('');
    setDepartureLocal('');
    setArrivalLocal('');
    setStatus('to_arrange');
    setDriverName('');
    setInfPerson(false);
    setInfClient(false);
    setInfDriver(false);
    setWorkedHours(null);
    setWorkedHoursError(null);
    setWorkedHoursLoading(false);
    setHoursFromLocal('');
    setHoursToLocal('');
    setTicketPrice('');
    setPrivatePaidTicket(false);
    setOwnContribution('');
    setIsTaronFlight(false);
    setVoucherPreviewCode(undefined);
    setVoucherAvailableCount(0);
    setInCreditsDebits(false);
    setCreditsDebitsProcessedDate('');
  };

  const handleClose = () => {
    onClose();
  };

  const handleCreate = async () => {
    if (!canSubmit || !selectedWorker) return;
    const payload = {
      workerId: selectedWorker.id,
      type,
      direction,
      flightNumber: flightNumber.trim() || undefined,
      routeFrom: routeFrom.trim() || undefined,
      routeTo: routeTo.trim() || undefined,
      departureAt: toIso(departureLocal),
      arrivalAt: toIso(arrivalLocal),
      ticketPrice: type === 'flight' && ticketPrice !== '' ? Number(ticketPrice) : undefined,
      privatePaidTicket: type === 'flight' ? privatePaidTicket : undefined,
      ownContribution: type === 'flight' && ownContribution !== '' ? Number(ownContribution) : undefined,
      hoursForTicket: hoursForTicket ? Number(hoursForTicket) : undefined,
      inCreditsDebits,
      creditsDebitsProcessedDate: inCreditsDebits && creditsDebitsProcessedDate ? creditsDebitsProcessedDate : undefined,
    } as any;
    const created = await createTravel(payload);
    if (type === 'flight' && isTaronFlight) {
      const voucherDirection = direction === 'coming' ? 'inbound' : 'outbound';
      const vouchers = await listVouchers({ unassignedOnly: true, direction: voucherDirection });
      const first = vouchers[0];
      if (first) {
        const person = `${selectedWorker.firstName} ${selectedWorker.lastName}`.trim();
        const flightDate = departureLocal ? (departureLocal.split('T')[0] || undefined) : undefined;
        await assignVoucher(first.id, { travelId: created.id, personName: person, flightDate });
      }
    }
    onCreate(created);
    reset();
  };

  const handleUpdate = async () => {
    if (!isEdit || !editId || !selectedWorker) return;
    const payload: any = {
      workerId: selectedWorker.id,
      type,
      direction,
      flightNumber: flightNumber.trim() || undefined,
      routeFrom: routeFrom.trim() || undefined,
      routeTo: routeTo.trim() || undefined,
      departureAt: toIso(departureLocal),
      arrivalAt: toIso(arrivalLocal),
      ticketPrice: type === 'flight' && ticketPrice !== '' ? Number(ticketPrice) : undefined,
      privatePaidTicket: type === 'flight' ? privatePaidTicket : undefined,
      ownContribution: type === 'flight' && ownContribution !== '' ? Number(ownContribution) : undefined,
      hoursForTicket: hoursForTicket ? Number(hoursForTicket) : undefined,
      inCreditsDebits,
      creditsDebitsProcessedDate: inCreditsDebits && creditsDebitsProcessedDate ? creditsDebitsProcessedDate : undefined,
    };
    const hasVoucher = !!assignedVoucher;
    const wantVoucher = isTaronFlight;

    // If turning off voucher and it was assigned, require unassign details first
    if (hasVoucher && !wantVoucher) {
      // Defer update until unassign dialog completes
      setUnassignOpen(true);
      pendingUpdateRef.current = { id: editId, payload };
      return;
    }

    // Proceed with update
    const updated = await updateTravel(editId, payload);

    // If turning on voucher and there is none, auto-assign the first available now
    if (!hasVoucher && wantVoucher) {
      try {
        const voucherDirection = direction === 'coming' ? 'inbound' : 'outbound';
        const v = await listVouchers({ unassignedOnly: true, direction: voucherDirection });
        const first = v[0];
        if (first) {
          const person = personName || (selectedWorker ? `${selectedWorker.firstName} ${selectedWorker.lastName}`.trim() : undefined);
          const flightDate = departureLocal ? (departureLocal.split('T')[0] || undefined) : undefined;
          await assignVoucher(first.id, { travelId: editId, personName: person, flightDate });
        }
      } catch {}
    }

    onUpdate?.(updated);
    reset();
  };

  useEffect(() => {
    if (!open) {
      setOptions([]);
      setSelectedWorker(null);
      setPersonName('');
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const list = await searchWorkers({ limit: 10000 });
        setOptions(list);
      } finally {
        setLoading(false);
      }
    })();
    (async () => {
      try {
        const voucherDirection = direction === 'coming' ? 'inbound' : 'outbound';
        const [v, s] = await Promise.all([
          listVouchers({ unassignedOnly: true, direction: voucherDirection }),
          getTravelSettings(),
        ]);
        setVoucherAvailableCount(v.length);
        setVoucherPreviewCode(v[0]?.code);
        setHoursPerTicketSetting(s.hoursPerTicket ?? 260);
        setCarAllowanceSetting(s.carAllowanceEuro ?? 50);
      } catch {}
    })();
    (async () => {
      if (isEdit && editId) {
        try {
          const assigned = await listVouchers({ assignedToTravelId: editId });
          setAssignedVoucher(assigned && assigned.length ? assigned[0] : null);
          if (assigned && assigned.length) setIsTaronFlight(true);
        } catch {}
      } else {
        setAssignedVoucher(null);
      }
    })();
    // Load existing record in edit mode
    (async () => {
      if (!isEdit || !editId) return;
      try {
        const t = await getTravel(editId);
        // worker
        const w = t.worker || {};
        const firstName = w.firstName || (t.person?.name?.split(' ')?.slice(0, -1).join(' ') || '');
        const lastName = w.lastName || (t.person?.name?.split(' ')?.slice(-1).join(' ') || '');
        const ws: WorkerSummary = { id: w.id || t.workerId, firstName, lastName, email: w.email, easyflexRegistrationNumber: w.easyflexRegistrationNumber } as any;
        setSelectedWorker(ws);
        setPersonName(`${ws.firstName} ${ws.lastName}`.trim());
        // basic fields
        setType(t.type);
        setDirection(t.direction);
        setFlightNumber(t.flightNumber || '');
        setRouteFrom(t.routeFrom || '');
        setRouteTo(t.routeTo || '');
        setDepartureLocal(t.departureAt ? new Date(t.departureAt).toISOString().slice(0,16) : '');
        setArrivalLocal(t.arrivalAt ? new Date(t.arrivalAt).toISOString().slice(0,16) : '');
        setTicketPrice(t.ticketPrice ? String(Number(t.ticketPrice)) : '');
        setPrivatePaidTicket(!!t.privatePaidTicket);
        setOwnContribution(t.ownContribution ? String(Number(t.ownContribution)) : '');
        setHoursForTicket(t.hoursForTicket != null ? String(t.hoursForTicket) : '');
        setInCreditsDebits(!!t.inCreditsDebits);
        setCreditsDebitsProcessedDate(t.creditsDebitsProcessedDate || '');
        // prefill hours range from departure/arrival
        const depDateOnly = t.departureAt ? new Date(t.departureAt).toISOString().slice(0,10) : '';
        const arrDateOnly = t.arrivalAt ? new Date(t.arrivalAt).toISOString().slice(0,10) : '';
        setHoursFromLocal(depDateOnly);
        setHoursToLocal(arrDateOnly || depDateOnly);
        // Trigger worked hours fetch with effective range
        const fromISO = depDateOnly ? toIsoFromDateInput(depDateOnly, false) : undefined;
        const toISO = (arrDateOnly || depDateOnly) ? toIsoFromDateInput(arrDateOnly || depDateOnly, true) : undefined;
        if (ws.id) {
          fetchWorkedHours(ws.id, { fromISO, toISO });
        }
      } catch {}
    })();
  }, [open]);

  useEffect(() => {
    (async () => {
      if (!open || !isTaronFlight) return;
      try {
        const voucherDirection = direction === 'coming' ? 'inbound' : 'outbound';
        const v = await listVouchers({ unassignedOnly: true, direction: voucherDirection });
        setVoucherAvailableCount(v.length);
        setVoucherPreviewCode(v[0]?.code);
      } catch {}
    })();
  }, [direction, isTaronFlight, open]);

  // Debounced fetch after blur helpers
  const hoursFetchTimeoutRef = useRef<number | undefined>(undefined);
  const clearHoursFetchTimeout = () => {
    if (hoursFetchTimeoutRef.current) {
      window.clearTimeout(hoursFetchTimeoutRef.current);
      hoursFetchTimeoutRef.current = undefined;
    }
  };
  const fetchWorkedHours = async (workerIdArg?: string, override?: { fromISO?: string; toISO?: string }) => {
    const id = workerIdArg || selectedWorker?.id;
    if (!id) return;
    setWorkedHoursLoading(true);
    setWorkedHoursError(null);
    try {
      const from = override?.fromISO ?? (hoursFromLocal ? toIsoFromDateInput(hoursFromLocal, false) : undefined);
      const to = override?.toISO ?? (hoursToLocal ? toIsoFromDateInput(hoursToLocal, true) : undefined);
      const data = await getWorkerWorkedHours(id, { from, to });
      setWorkedHours(data);
    } catch (e: any) {
      setWorkedHoursError(e?.message || 'Failed to fetch worked hours');
      setWorkedHours(null);
    } finally {
      setWorkedHoursLoading(false);
    }
  };
  // Compute effective time range based on Hours inputs or (fallback) Leaving/Arrival
  const currentEffectiveRangeISO = (): { fromISO?: string; toISO?: string } => {
    const fromISO = hoursFromLocal
      ? toIsoFromDateInput(hoursFromLocal, false)
      : (departureLocal ? toIsoFromDateInput((departureLocal.split('T')[0] || ''), false) : undefined);
    const toISO = hoursToLocal
      ? toIsoFromDateInput(hoursToLocal, true)
      : (arrivalLocal
          ? toIsoFromDateInput((arrivalLocal.split('T')[0] || ''), true)
          : (departureLocal ? toIsoFromDateInput((departureLocal.split('T')[0] || ''), true) : undefined)
        );
    return { fromISO, toISO };
  };
  const scheduleHoursFetch = () => {
    clearHoursFetchTimeout();
    const override = currentEffectiveRangeISO();
    hoursFetchTimeoutRef.current = window.setTimeout(() => fetchWorkedHours(undefined, override), 1000);
  };
  useEffect(() => () => clearHoursFetchTimeout(), []);

  // Debounced worker search for Autocomplete
  const workerSearchTimeoutRef = useRef<number | undefined>(undefined);
  const clearWorkerSearchTimeout = () => {
    if (workerSearchTimeoutRef.current) {
      window.clearTimeout(workerSearchTimeoutRef.current);
      workerSearchTimeoutRef.current = undefined;
    }
  };
  const scheduleWorkerSearch = (query: string) => {
    clearWorkerSearchTimeout();
    workerSearchTimeoutRef.current = window.setTimeout(async () => {
      setLoading(true);
      try {
        const list = await searchWorkers({ search: query, limit: 100 });
        setOptions(list);
      } finally {
        setLoading(false);
      }
    }, 1000);
  };
  useEffect(() => () => clearWorkerSearchTimeout(), []);

  // Projection helpers
  const computeProjection = (data: WorkedHoursResponseData | null): { total?: number; perOrg?: Record<string, number> } => {
    if (!data) return {};
    const now = new Date();
    // Determine effective start and end
    const effectiveFrom = data.from ? new Date(data.from) : (hoursFromLocal ? new Date(toIsoFromDateInput(hoursFromLocal, false)) : undefined);
    const effectiveTo = data.to ? new Date(data.to) : (hoursToLocal ? new Date(toIsoFromDateInput(hoursToLocal, true)) : undefined);
    if (!effectiveTo || effectiveTo <= now) return {};
    const start = effectiveFrom && !isNaN(effectiveFrom.getTime()) ? effectiveFrom : undefined;
    const end = !isNaN(effectiveTo.getTime()) ? effectiveTo : undefined;
    if (!end) return {};
    const observedUntil = now < end ? now : end;
    if (!start) return {}; // cannot compute days base reliably
    const observedDays = daysBetween(start, observedUntil);
    const plannedDays = daysBetween(start, end);
    if (observedDays <= 0 || plannedDays <= 0) return {};
    const observedTotal = Number(data.hours || 0);
    const dailyRate = observedTotal / observedDays;
    const projectedTotal = dailyRate * plannedDays;
    // Per-organization
    const perOrg: Record<string, number> = {};
    (data.perOrganization || []).forEach(org => {
      const obs = Number(org.hours || 0);
      const rate = obs / observedDays;
      perOrg[org.organization] = rate * plannedDays;
    });
    return { total: projectedTotal, perOrg };
  };

  const projected = useMemo(() => computeProjection(workedHours), [workedHours, hoursFromLocal, hoursToLocal]);
  // Initialize hoursForTicket from projected/observed when first loading hours
  useEffect(() => {
    if (!workedHours) return;
    if (hoursForTicket !== '') return; // don't overwrite user input
    const base = (projected.total && projected.total > 0) ? projected.total : workedHours.hours || 0;
    const rounded = Math.round(base);
    setHoursForTicket(String(rounded));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workedHours, projected.total]);

  // Settlement computation
  // Allowance calculated based on hoursForTicket and hoursPerTicketSetting
  const allowanceAmount = useMemo(() => {
    const hours = Number(hoursForTicket || '0');
    const threshold = Number(hoursPerTicketSetting) || 260;
    if (!threshold || threshold <= 0) return 0;
    const ratio = Math.max(0, Math.min(1, hours / threshold));
    if (type === 'flight') {
      const price = Number(ticketPrice || '0');
      const priceValid = Number.isFinite(price) && price >= 0 ? price : 0;
      const amount = priceValid * ratio;
      return Math.round((amount + Number.EPSILON) * 100) / 100;
    }
    if (type === 'car_self' || type === 'car_optima') {
      const base = Number(carAllowanceSetting || 0);
      const baseValid = Number.isFinite(base) && base >= 0 ? base : 0;
      const amount = baseValid * ratio;
      return Math.round((amount + Number.EPSILON) * 100) / 100;
    }
    return 0;
  }, [type, ticketPrice, hoursForTicket, hoursPerTicketSetting, carAllowanceSetting]);

  const computeTotalSettlement = useMemo(() => {
    const price = Number(ticketPrice || '0');
    const own = Number(ownContribution || '0');
    const priceValid = Number.isFinite(price) && price >= 0 ? price : 0;
    const ownValid = Number.isFinite(own) && own >= 0 ? own : 0;
    const ownClamped = Math.min(ownValid, priceValid);
    const allowance = allowanceAmount;
    // Cars: total is payment to worker (allowance) → +
    if (type === 'car_self' || type === 'car_optima') {
      return () => Math.round((allowance + Number.EPSILON) * 100) / 100; // + give to worker
    }
    if (type !== 'flight') return () => undefined as number | undefined;
    // Flights: Private paid ticket → worker paid scenario → + reimbursement
    if (privatePaidTicket) {
      // company reimburses up to allowance, capped by (price - ownContribution)
      const reimbursable = Math.max(priceValid - ownClamped, 0);
      const reimbursement = Math.min(allowance, reimbursable);
      return () => Math.round((reimbursement + Number.EPSILON) * 100) / 100; // + give to worker
    }
    // Company paid ticket → deduct (price - allowance) from worker → −
    const diff = Math.max(priceValid - allowance, 0);
    return () => -Math.round((diff + Number.EPSILON) * 100) / 100; // − deduct from worker
  }, [ticketPrice, ownContribution, privatePaidTicket, type, allowanceAmount, hoursForTicket]);

  function daysBetween(a: Date, b: Date): number {
    const ms = b.getTime() - a.getTime();
    return ms > 0 ? ms / (1000 * 60 * 60 * 24) : 0;
  }
  function toIsoFromDateInput(dateStr: string, endOfDay: boolean): string {
    // dateStr is expected as yyyy-MM-dd
    const [y, m, d] = dateStr.split('-').map(n => parseInt(n, 10));
    const dt = new Date();
    dt.setFullYear(y, (m || 1) - 1, d || 1);
    if (endOfDay) {
      dt.setHours(23, 59, 59, 999);
    } else {
      dt.setHours(0, 0, 0, 0);
    }
    return dt.toISOString();
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Travel' : 'Add Travel'}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel id="type-label">Type</InputLabel>
                <Select labelId="type-label" label="Type" value={type} onChange={(e: SelectChangeEvent) => setType(e.target.value as TravelType)}>
                  <MenuItem value="flight">Flight</MenuItem>
                  <MenuItem value="car_optima">Optima car</MenuItem>
                  <MenuItem value="car_self">Self-arranged car</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel id="segment-label">Segment</InputLabel>
                <Select labelId="segment-label" label="Segment" value={direction} onChange={(e: SelectChangeEvent) => setDirection(e.target.value as any)}>
                  <MenuItem value="leaving">Leaving</MenuItem>
                  <MenuItem value="coming">Coming</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={8}>
              <Stack spacing={1.5}>
              <Autocomplete
                fullWidth
                loading={loading}
                options={options}
                filterOptions={(x) => x}
                getOptionLabel={(o) => `${o.firstName} ${o.lastName}`.trim()}
                value={selectedWorker}
                onChange={(_, val) => {
                  setSelectedWorker(val);
                  setPersonName(val ? `${val.firstName} ${val.lastName}` : '');
                  if (val) {
                    clearHoursFetchTimeout();
                    const eff = currentEffectiveRangeISO();
                    if (eff.fromISO || eff.toISO) {
                      fetchWorkedHours(val.id, eff);
                    } else {
                      // No inputs -> default timeframe
                      fetchWorkedHours(val.id);
                    }
                  } else {
                    clearHoursFetchTimeout();
                  }
                }}
                onInputChange={(_, value) => {
                  setPersonName(value);
                  clearWorkerSearchTimeout();
                  const q = (value || '').trim();
                  if (q.length > 4) {
                    scheduleWorkerSearch(q);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Person (search workers)"
                    placeholder="Type a name, email or EF number"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.firstName} {option.lastName}
                    {option.easyflexRegistrationNumber ? `  (EF #${option.easyflexRegistrationNumber})` : ''}
                  </li>
                )}
              />
              {/* ID document button temporarily removed */}
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel id="status-label">Status</InputLabel>
                <Select labelId="status-label" label="Status" value={status} onChange={(e) => setStatus(e.target.value as TravelStatus)}>
                  <MenuItem value="to_arrange">To arrange</MenuItem>
                  <MenuItem value="booked">Booked</MenuItem>
                  <MenuItem value="past">Past</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Flight-only fields still visible but optional */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth label="Flight number" value={flightNumber} onChange={(e) => setFlightNumber(e.target.value)} />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField fullWidth label="From" value={routeFrom} onChange={(e) => setRouteFrom(e.target.value.toUpperCase())} placeholder="AMS" />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField fullWidth label="To" value={routeTo} onChange={(e) => setRouteTo(e.target.value.toUpperCase())} placeholder="BCN" />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField fullWidth label="Driver name (if flight)" value={driverName} onChange={(e) => setDriverName(e.target.value)} />
            </Grid>

            {/* Leaving/Arrival own row */}
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Leaving"
                    type="datetime-local"
                    value={departureLocal}
                    onChange={(e) => {
                      const v = e.target.value;
                      setDepartureLocal(v);
                      if (!hoursToLocal && v) {
                        const dateOnly = v.split('T')[0] || '';
                        if (dateOnly) {
                          setHoursToLocal(dateOnly);
                          // Immediately compute worked hours with auto-filled 'to' (end-of-day)
                          clearHoursFetchTimeout();
                          const toISO = toIsoFromDateInput(dateOnly, true);
                          const fromISO = hoursFromLocal ? toIsoFromDateInput(hoursFromLocal, false) : undefined;
                          fetchWorkedHours(undefined, { fromISO, toISO });
                        }
                      }
                    }}
                    onBlur={scheduleHoursFetch}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Arrival"
                    type="datetime-local"
                    value={arrivalLocal}
                    onChange={(e) => setArrivalLocal(e.target.value)}
                    onBlur={scheduleHoursFetch}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Taron tickets */}
            {type === 'flight' && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={<Switch checked={isTaronFlight} onChange={(e) => {
                      const on = e.target.checked;
                      setIsTaronFlight(on);
                      // Refresh availability preview via API
                      (async () => {
                        try {
                          const voucherDirection = direction === 'coming' ? 'inbound' : 'outbound';
                          const v = await listVouchers({ unassignedOnly: true, direction: voucherDirection });
                          setVoucherAvailableCount(v.length);
                          setVoucherPreviewCode(v[0]?.code);
                        } catch {}
                      })();
                    }} />}
                    label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><LocalActivityIcon fontSize="small" /> Taron flight</Box>}
                  />
                  {isTaronFlight && !assignedVoucher && (
                    <>
                      <Chip variant="outlined" icon={<LocalActivityIcon />} label={`${voucherAvailableCount} voucher${voucherAvailableCount === 1 ? '' : 's'} left`} />
                      {voucherPreviewCode && (
                        <Chip color="secondary" variant="outlined" icon={<LocalActivityIcon />} label={`Will assign: ${voucherPreviewCode}`} />
                      )}
                    </>
                  )}
                  {isEdit && assignedVoucher && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ fontWeight: 600 }}>Assigned voucher:</Box>
                      <Chip label={assignedVoucher.code} size="small" color="primary" />
                      <Button variant="outlined" size="small" onClick={() => setUnassignOpen(true)}>Unassign</Button>
                    </Box>
                  )}
                </Box>
              </Grid>
            )}

            {/* Inform toggles (moved to top section) */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControlLabel control={<Switch checked={infPerson} onChange={(e) => setInfPerson(e.target.checked)} />} label="Person informed" />
                <FormControlLabel control={<Switch checked={infClient} onChange={(e) => setInfClient(e.target.checked)} />} label="Client informed" />
                <FormControlLabel control={<Switch checked={infDriver} onChange={(e) => setInfDriver(e.target.checked)} />} label="Driver informed" />
              </Box>
            </Grid>

            {/* Costs section */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <AttachMoneyIcon fontSize="small" />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Costs</Typography>
              </Box>
            </Grid>
            {type === 'flight' && (
              <>
                {/* Row 1: Ticket price + Optima paid */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                      sx={{ minWidth: 260, flex: 1 }}
                      label="Ticket price (€)"
                      type="number"
                      value={ticketPrice}
                      onChange={(e) => setTicketPrice(e.target.value)}
                      inputProps={{ step: '0.01', min: '0' }}
                      placeholder="0.00"
                    />
                    <FormControlLabel control={<Switch checked={privatePaidTicket} onChange={(e) => setPrivatePaidTicket(e.target.checked)} />} label="Private paid ticket" />
                  </Box>
                </Grid>

                {/* Row 2: Own contribution + Allowance (calculated) + Total */}
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    {/* <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        label="Own contribution (€)"
                        type="number"
                        value={ownContribution}
                        onChange={(e) => setOwnContribution(e.target.value)}
                        inputProps={{ step: '0.01', min: '0' }}
                        placeholder="0.00"
                      />
                    </Grid> */}
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        label="Optima costs (€)"
                        type="text"
                        value={allowanceAmount.toFixed(2)}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={8}>
                      <Box sx={{ p: 1.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">Worker cost (put in creditsdebits) (− deduct from worker, + give to worker)</Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: (computeTotalSettlement() ?? 0) > 0 ? 'success.main' : 'error.main' }}>
                          {(() => {
                            const v = computeTotalSettlement();
                            if (v === undefined) return '—';
                            const sign = v < 0 ? '−' : '+';
                            const abs = Math.abs(v);
                            return `${sign} € ${abs.toFixed(2)}`;
                          })()}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </>
            )}

            {/* Credits / Debits */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <FormControlLabel control={<Switch checked={inCreditsDebits} onChange={(e) => setInCreditsDebits(e.target.checked)} />} label="In credits/debits?" />
                <TextField
                  label="Processed date"
                  type="date"
                  value={creditsDebitsProcessedDate}
                  onChange={(e) => setCreditsDebitsProcessedDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={!inCreditsDebits}
                />
              </Box>
            </Grid>


            {/* Hours range section title */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <CalendarMonthIcon fontSize="small" />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Hours range</Typography>
              </Box>
            </Grid>
            {/* Hours range controls */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Hours from"
                type="date"
                value={hoursFromLocal}
                onChange={(e) => setHoursFromLocal(e.target.value)}
                onBlur={scheduleHoursFetch}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Hours to"
                type="date"
                value={hoursToLocal}
                onChange={(e) => setHoursToLocal(e.target.value)}
                onBlur={scheduleHoursFetch}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Worked hours summary on its own line */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <InfoIcon fontSize="small" />
                  <Box sx={{ fontWeight: 700 }}>Worked hours</Box>
                </Box>
                {!selectedWorker && (
                  <Box color="text.secondary">Select a worker to see hours</Box>
                )}
                {selectedWorker && workedHoursLoading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CircularProgress size={18} /> Loading hours…</Box>
                )}
                {selectedWorker && workedHoursError && (
                  <Box color="error.main">{workedHoursError}</Box>
                )}
                {selectedWorker && !workedHoursLoading && !workedHoursError && workedHours && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Chip icon={<AccessTimeIcon />} label={`Observed: ${Math.round(workedHours.hours)} h`} color="primary" variant="outlined" sx={{ fontWeight: 700 }} />
                    {projected.total !== undefined && projected.total > 0 && (
                      <Chip icon={<TrendingUpIcon />} label={`Projected: ${Math.round(projected.total)} h`} color="success" sx={{ fontWeight: 700 }} />
                    )}
                    <Chip icon={<CalendarMonthIcon />} label={workedHours.from && workedHours.to ? `${new Date(workedHours.from).toLocaleDateString()} – ${new Date(workedHours.to).toLocaleDateString()}` : 'Last 6 months'} variant="outlined" />
                    {/* Hours for ticket input */}
                    <TextField
                      size="small"
                      label={`Hours for ticket (≥ ${hoursPerTicketSetting})`}
                      type="number"
                      value={hoursForTicket}
                      onChange={(e) => {
                        setHoursForTicket(e.target.value);
                      }}
                      inputProps={{ min: '0', step: '1' }}
                    />
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Per-month, per-organization table */}
            {selectedWorker && workedHours && workedHours.perOrganization && workedHours.perOrganization.length > 0 && (
              <Grid item xs={12}>
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ fontWeight: 600, mb: 1 }}>Hours by organization and month</Box>
                  <OrganizationMonthTable data={workedHours} projectedPerOrg={projected.perOrg} projectedTotal={projected.total} />
                </Box>
              </Grid>
            )}

            {/* removed bottom toggles (moved above) */}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        {isEdit ? (
          <Button variant="contained" onClick={handleUpdate} disabled={!canSubmit}>Save</Button>
        ) : (
          <Button variant="contained" onClick={handleCreate} disabled={!canSubmit}>Add</Button>
        )}
      </DialogActions>
      <UnassignVoucherDialog
        open={unassignOpen}
        onClose={() => setUnassignOpen(false)}
        onConfirm={async (payload: UnassignVoucherPayload) => {
          if (!assignedVoucher) return;
          try {
            await unassignVoucher(assignedVoucher.id, payload);
            setAssignedVoucher(null);
            setUnassignOpen(false);
            const v = await listVouchers({ unassignedOnly: true });
            setVoucherAvailableCount(v.length);
            setVoucherPreviewCode(v[0]?.code);

            // If unassign was triggered by Save flow, continue the pending update now
            const pending = pendingUpdateRef.current;
            if (pending && pending.id) {
              try {
                const updated = await updateTravel(pending.id, pending.payload);
                if (onUpdate) onUpdate(updated);
              } finally {
                pendingUpdateRef.current = null;
                reset();
              }
            }
          } catch {
            // ignore
          }
        }}
      />
    </Dialog>
  );
}

// Renders a table with rows for each month and columns per organization
const OrganizationMonthTable = React.memo(function OrganizationMonthTable({ data, projectedPerOrg, projectedTotal }: { data: WorkedHoursResponseData, projectedPerOrg?: Record<string, number>, projectedTotal?: number }) {
  // Build a sorted list of months present in total breakdown or any org breakdown
  const monthKey = (y: number, m: number) => `${y}-${String(m).padStart(2, '0')}`;

  const monthSet = new Set<string>();
  data.breakdown?.forEach(b => monthSet.add(monthKey(b.year, b.month)));
  data.perOrganization?.forEach(org => org.breakdown?.forEach(b => monthSet.add(monthKey(b.year, b.month))));

  const months = Array.from(monthSet)
    .map(k => ({
      key: k,
      year: parseInt(k.split('-')[0], 10),
      month: parseInt(k.split('-')[1], 10),
    }))
    .sort((a, b) => a.year !== b.year ? a.year - b.year : a.month - b.month);

  // Determine organization columns
  const orgs = (data.perOrganization || []).map(p => p.organization);
  const orgNames = Array.from(new Set(orgs)).sort((a, b) => a.localeCompare(b));

  // Fast lookup maps
  const perOrgMap = new Map<string, Map<string, number>>(); // org -> (monthKey -> hours)
  data.perOrganization?.forEach(org => {
    const m = new Map<string, number>();
    org.breakdown?.forEach(b => m.set(monthKey(b.year, b.month), b.hours ?? 0));
    perOrgMap.set(org.organization, m);
  });

  const formatMonth = (y: number, m: number) => {
    const d = new Date(Date.UTC(y, m - 1, 1));
    return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  };

  const fmt = (v: number | undefined) => v === undefined ? '' : (Math.round((v + Number.EPSILON) * 100) / 100).toString();

  return (
    <Table size="small" sx={{ tableLayout: 'fixed' }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: 140, fontWeight: 600 }}>Month</TableCell>
          {orgNames.map(name => (
            <TableCell key={name} align="right" sx={{ fontWeight: 600 }}>{name}</TableCell>
          ))}
          <TableCell align="right" sx={{ fontWeight: 600, width: 100 }}>Total</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {months.map(({ key, year, month }) => {
          const perOrgValues = orgNames.map(name => perOrgMap.get(name)?.get(key) || 0);
          const total = perOrgValues.reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0);
          return (
            <TableRow key={key} hover>
              <TableCell>{formatMonth(year, month)}</TableCell>
              {orgNames.map(name => (
                <TableCell key={`${key}-${name}`} align="right">{fmt(perOrgMap.get(name)?.get(key) || 0)}</TableCell>
              ))}
              <TableCell align="right">{fmt(total)}</TableCell>
            </TableRow>
          );
        })}
        {/* Footer totals */}
        <TableRow sx={{ bgcolor: (theme) => theme.palette.action.hover }}>
          <TableCell sx={{ fontWeight: 600 }}>Sum</TableCell>
          {orgNames.map(name => {
            const sum = Array.from(perOrgMap.get(name)?.values() || []).reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0);
            return <TableCell key={`sum-${name}`} align="right" sx={{ fontWeight: 600 }}>{fmt(sum)}</TableCell>;
          })}
          <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(data.hours)}</TableCell>
        </TableRow>
        {projectedPerOrg && projectedTotal !== undefined && (
          <TableRow sx={{ bgcolor: (theme) => (theme.palette.warning.light + '33') }}>
            <TableCell sx={{ fontWeight: 600 }}>Projected</TableCell>
            {orgNames.map(name => (
              <TableCell key={`proj-${name}`} align="right" sx={{ fontWeight: 600 }}>{fmt(projectedPerOrg[name])}</TableCell>
            ))}
            <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(projectedTotal)}</TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
});
